import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';

const DeleteConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Delete Confirmation",
  itemName = "this item",
  dependencies = null,
  isLoading = false
}) => {
  const hasDependencies = dependencies && dependencies.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasDependencies ? (
            <WarningIcon color="warning" />
          ) : (
            <DeleteIcon color="error" />
          )}
          <Typography variant="h6" component="span">
            {hasDependencies ? "Cannot Delete" : title}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {hasDependencies ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                Cannot delete {itemName} because it has associated records.
              </Typography>
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please update or remove the following associated records first, then try deleting again:
            </Typography>
            
            {dependencies.map((dep, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip 
                    label={`${dep.count} ${dep.entity}`} 
                    color="warning" 
                    size="small" 
                  />
                </Box>
                
                <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                  {dep.records.map((record, recordIndex) => (
                    <ListItem key={recordIndex} sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={record}
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                  {dep.count > dep.records.length && (
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`... and ${dep.count - dep.records.length} more`}
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          fontStyle: 'italic',
                          color: 'text.disabled'
                        }}
                      />
                    </ListItem>
                  )}
                </List>
                
                {index < dependencies.length - 1 && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body1">
            Are you sure you want to delete {itemName}? This action cannot be undone.
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {hasDependencies ? "Close" : "Cancel"}
        </Button>
        {!hasDependencies && (
          <Button 
            onClick={onConfirm} 
            color="error" 
            variant="contained"
            disabled={isLoading}
            startIcon={<DeleteIcon />}
          >
            {isLoading ? "Deleting..." : "Delete"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog; 